import React, { useState } from 'react'
import { Box, Card, CardContent, CardHeader, CircularProgress } from '@mui/material'
import { createNewOrg, type NewOrganizationInformation } from 'services'
import { useGlobalMessageHandler, cardStyles, OrgBasicInfoFields, IconButtonWithTooltip } from 'components'
import { useNavigate } from 'react-router-dom'
import { formatErrorFieldMessages } from 'interfaces'
import SaveIcon from '@mui/icons-material/Save'

export const NewOrganizationInfoBlock: React.FC = () => {
  const [newOrgInfo, setNewOrgInfo] = useState<NewOrganizationInformation>({
    country: null,
    orgTypeId: null,
    organizationName: '',
    organizationNameLocal: '',
    parentOrganization: null
  })
  const [loadingClick, setLoadingClick] = useState(false)
  const navigate = useNavigate()
  const { showErrorMessage, clearMessages } = useGlobalMessageHandler()
  const requiredFields = ['organizationName', 'organizationNameLocal', 'orgTypeId']

  const toggleLoadingClick = (): void => {
    setLoadingClick(prevState => !prevState)
  }

  const handlePostClick = (): void => {
    toggleLoadingClick()
    const payload = {
      organizationName: newOrgInfo.organizationName,
      organizationNameLocal: newOrgInfo.organizationNameLocal,
      countryId: (newOrgInfo.country != null) ? newOrgInfo.country.id : null,
      parentOrgId: newOrgInfo.parentOrganization?.id,
      organizationTypeId: newOrgInfo.orgTypeId !== 0 ? newOrgInfo.orgTypeId : null
    }

    void createNewOrg(payload).then(value => {
      clearMessages()
      if (value.succeeded && ((value.result?.id) != null)) {
        navigate(`/editOrganization/${value.result?.id}/`)
      } else {
        showErrorMessage(formatErrorFieldMessages(value.errors, prettierKeyNames))
      }
    }).catch((reason: Error) => {
      showErrorMessage('Unexpected error creating organization')
    }).finally(() => {
      toggleLoadingClick()
    })
  }

  const prettierKeyNames: Record<string, string> = {
    organizationName: 'Organization Name',
    organizationNameLocal: 'Organization Local Name',
    countryId: 'Organization country',
    parentOrgId: 'Parent organization',
    organizationTypeId: 'Organization Type'

  }

  const isFieldEmpty = (field: string): boolean => {
    if (!(field in newOrgInfo)) {
      throw new Error(`Field ${field} does not exist in NewOrganizationInformation`)
    }
    return newOrgInfo[field as keyof NewOrganizationInformation] == null || newOrgInfo[field as keyof NewOrganizationInformation] === ''
  }

  const areFieldsValid = (): boolean => requiredFields.every((field) => !isFieldEmpty(field))

  const validateFields = (): boolean => {
    if (areFieldsValid()) {
      handlePostClick()
    }

    return false
  }
  return (
        <Card sx={cardStyles}>
            <CardHeader title={'Basic Information'} titleTypographyProps={{ variant: 'h6' }} subheader={'Fill in the required basic information fields for a new organization:'} subheaderTypographyProps={{ variant: 'subtitle1' }}/>
            <CardContent>
                <OrgBasicInfoFields loading={loadingClick} value={newOrgInfo} updateValue={setNewOrgInfo} isFieldEmpty={isFieldEmpty} disabled={false} />
            </CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'end' }}>
            {loadingClick
              ? <CircularProgress/>
              : <IconButtonWithTooltip label="Save Organization" color="primary" onClick={validateFields} disabled={loadingClick || !areFieldsValid() } icon={<SaveIcon/>}/>
            }
          </Box>
        </Card>
  )
}
